import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addNodesInTree,
  getNodeInTree,
  updateNodeInTree,
} from '../../../components/Tree/helpers/helpers';
import { getPathById } from '../../../utils/tree/treeNode';
import { selectTreeDataByTreeId, updateData } from '../../slices/tree';
import { getItem } from '../api/items';
import { selectSearchableCatalogIds } from '../../selectors/catalog/ids';
import { countrySpecificToItemNodes } from '../../../utils/tree/nodeMappers/toItemNodes';
import type { NodeBase, NodeItem } from '../../../entities/Node';
import type { RootState } from '../..';

export const fetchItem = createAsyncThunk(
  'tree/fetchItem',
  async ({ tree, node }: { tree: string; node: NodeBase }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      catalogId,
      id,
      item: { itemNo, itemType },
    } = node;
    const path = getPathById(id);

    const searchableCatalogs: string[] = selectSearchableCatalogIds(
      getState() as RootState,
    );

    const isSearchableCatalog = searchableCatalogs.includes(catalogId);

    const item = await dispatch(
      getItem({
        catalogId: isSearchableCatalog ? catalogId : '',
        itemNo,
        itemType: itemType || '',
      }),
    ).unwrap();

    const currentTreeData = selectTreeDataByTreeId(getState() as RootState, tree);
    const currentNodeItem = getNodeInTree({
      treeData: currentTreeData,
      path,
    }) as { node: NodeItem; path: string[]; treeIndex: number };

    const { itemCountrySpecificList, ...newItem } = item;

    let treeData = updateNodeInTree({
      node: {
        ...currentNodeItem.node,
        item: { ...currentNodeItem.node.item, ...newItem },
      },
      path,
      treeData: currentTreeData,
    }) as NodeBase[];

    if (!isSearchableCatalog && itemCountrySpecificList?.length) {
      const childNodes = countrySpecificToItemNodes({
        catalogId,
        isLeaf: true,
        items: itemCountrySpecificList,
        level: node.level + 1,
        parentId: node.id,
        inDraft: currentNodeItem.node?.inDraft,
        isInSuggestion: currentNodeItem.node?.isInSuggestion,
      });

      treeData = addNodesInTree(childNodes, treeData).treeData;
    }

    return dispatch(updateData(tree, treeData, { selectNodeAtPath: path }));
  },
);
